/**
 * CreditPanel — shared between Doctor Dashboard and Mom Dashboard.
 *
 * Strict rules (Step 8):
 *  - Backend is the SOLE source of credit truth. This component only renders.
 *  - No hardcoded credit costs / package prices — everything is read from the
 *    `/api/credits/me` and `/api/credits/packages` responses.
 *  - "Unlimited" / "Grandfathered" users see a badge instead of a balance.
 *  - "Free 4K right" status is rendered explicitly (available / used).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { creditsApi, ApiError } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Coins,
  Gift,
  Infinity as InfinityIcon,
  ShieldCheck,
  RefreshCw,
  Loader2,
  ShoppingCart,
  History,
  Sparkles,
  CheckCircle2,
} from 'lucide-react';

const MODE_BADGES = {
  unlimited: {
    text: 'Sınırsız kullanım aktif',
    icon: InfinityIcon,
    className: 'bg-emerald-500/10 text-emerald-700 border-emerald-200',
    testid: 'credit-mode-badge-unlimited',
  },
  grandfathered: {
    text: 'Legacy kullanım hakkı aktif',
    icon: ShieldCheck,
    className: 'bg-indigo-500/10 text-indigo-700 border-indigo-200',
    testid: 'credit-mode-badge-grandfathered',
  },
  normal: {
    text: 'Standart hesap',
    icon: Coins,
    className: 'bg-slate-500/10 text-slate-700 border-slate-200',
    testid: 'credit-mode-badge-normal',
  },
};

export function CreditPanel({ variant = 'doctor', onAccountChange }) {
  const [account, setAccount] = useState(null);
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState(null);
  const [buyingPackageId, setBuyingPackageId] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [acc, pkg, hist] = await Promise.all([
        creditsApi.getMyCredits(),
        creditsApi.getPackages().catch(() => ({ packages: [] })),
        creditsApi.getMyHistory(20).catch(() => ({ entries: [] })),
      ]);
      setAccount(acc);
      setPackages(pkg.packages || []);
      setHistory(hist.entries || []);
      onAccountChange && onAccountChange(acc);
    } catch (e) {
      toast.error(e?.message || 'Kredi bilgisi yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [onAccountChange]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleRedeem = async (e) => {
    e?.preventDefault?.();
    const code = (redeemCode || '').trim();
    if (!code) {
      setRedeemError('Lütfen bir kod girin.');
      return;
    }
    setRedeeming(true);
    setRedeemError(null);
    try {
      const result = await creditsApi.redeem(code);
      toast.success('Kod başarıyla kullanıldı.');
      setRedeemCode('');
      // Backend returns the fresh account inline; refresh anyway for ledger.
      if (result?.account) setAccount(result.account);
      onAccountChange && onAccountChange(result?.account || account);
      loadAll();
    } catch (err) {
      // Map backend error codes (404/410/409/403) to friendly TR messages.
      const errMsg = mapRedeemError(err);
      // Defensive: show in BOTH the inline error label AND a toast so the
      // user never misses the failure even if the inline element is offscreen.
      setRedeemError(errMsg);
      toast.error(errMsg);
    } finally {
      setRedeeming(false);
    }
  };

  const handleBuyRequest = async (pkg) => {
    setBuyingPackageId(pkg.id);
    try {
      await creditsApi.buyRequest({
        package_id: pkg.id,
        contact_method: 'email',
      });
      toast.success(
        'Talebiniz alındı. Ödeme/onay sonrası krediniz tanımlanacak.',
        { duration: 5000 },
      );
      // Intentionally NOT refreshing balance — credits are not granted yet.
    } catch (err) {
      toast.error(err?.message || 'Talep oluşturulamadı.');
    } finally {
      setBuyingPackageId(null);
    }
  };

  if (loading) {
    return (
      <Card data-testid="credit-panel-loading">
        <CardContent className="flex items-center gap-2 py-6 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Kredi bilgisi yükleniyor…
        </CardContent>
      </Card>
    );
  }

  if (!account) {
    return (
      <Card data-testid="credit-panel-empty">
        <CardContent className="py-6 text-sm text-slate-500">
          Kredi hesabınız bulunamadı.{' '}
          <button
            className="underline text-emerald-700"
            onClick={loadAll}
            data-testid="credit-panel-retry-btn"
          >
            Tekrar dene
          </button>
        </CardContent>
      </Card>
    );
  }

  const mode = account.credit_mode || 'normal';
  const badge = MODE_BADGES[mode] || MODE_BADGES.normal;
  const BadgeIcon = badge.icon;
  const isUnlimited = mode === 'unlimited' || mode === 'grandfathered';
  const free4kAvailable =
    !isUnlimited &&
    (account.free_4k_generation_total || 0) > 0 &&
    !account.free_4k_generation_used;
  const free4kUsed =
    !isUnlimited &&
    (account.free_4k_generation_total || 0) > 0 &&
    account.free_4k_generation_used;

  return (
    <div className="space-y-4" data-testid={`credit-panel-${variant}`}>
      <Card data-testid="credit-panel-summary">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Coins className="h-4 w-4 text-emerald-600" />
            Kredi Durumu
          </CardTitle>
          <button
            type="button"
            onClick={loadAll}
            className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
            data-testid="credit-panel-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Yenile
          </button>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant="outline"
              className={`${badge.className} px-3 py-1`}
              data-testid={badge.testid}
            >
              <BadgeIcon className="h-3.5 w-3.5 mr-1" />
              {badge.text}
            </Badge>
            {free4kAvailable && (
              <Badge
                variant="outline"
                className="bg-amber-500/10 text-amber-700 border-amber-200"
                data-testid="credit-panel-free4k-available"
              >
                <Gift className="h-3.5 w-3.5 mr-1" />
                1 ücretsiz 4K hakkınız var
              </Badge>
            )}
            {free4kUsed && (
              <Badge
                variant="outline"
                className="bg-slate-500/10 text-slate-600 border-slate-200"
                data-testid="credit-panel-free4k-used"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                Ücretsiz 4K hakkı kullanıldı
              </Badge>
            )}
          </div>

          {!isUnlimited && (
            <div
              className="flex items-baseline gap-2"
              data-testid="credit-panel-balance-row"
            >
              <span
                className="text-3xl font-semibold text-slate-900 tabular-nums"
                data-testid="credit-panel-balance"
              >
                {account.balance ?? 0}
              </span>
              <span className="text-sm text-slate-500">kredi mevcut</span>
              {(account.reserved_balance || 0) > 0 && (
                <span
                  className="text-xs text-slate-400 ml-2"
                  data-testid="credit-panel-reserved"
                >
                  ({account.reserved_balance} rezerve)
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="credit-panel-redeem">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-600" />
            Hediye Kodu Kullan
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRedeem} className="flex gap-2 items-start">
            <div className="flex-1">
              <Input
                value={redeemCode}
                onChange={(e) => {
                  setRedeemCode(e.target.value.toUpperCase());
                  setRedeemError(null);
                }}
                placeholder="ÖR: NEOB-XXXX-XXXX"
                disabled={redeeming}
                data-testid="credit-panel-redeem-input"
              />
              {redeemError && (
                <p
                  className="text-xs text-red-600 mt-1"
                  data-testid="credit-panel-redeem-error"
                >
                  {redeemError}
                </p>
              )}
            </div>
            <Button
              type="submit"
              disabled={redeeming || !redeemCode.trim()}
              data-testid="credit-panel-redeem-submit-btn"
            >
              {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kullan'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {!isUnlimited && packages.length > 0 && (
        <Card data-testid="credit-panel-packages">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShoppingCart className="h-4 w-4 text-indigo-600" />
              Kredi Paketleri
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {packages.map((p) => (
              <div
                key={p.id}
                className="rounded-lg border border-slate-200 p-3 flex flex-col gap-2"
                data-testid={`credit-package-${p.id}`}
              >
                <div className="flex items-baseline justify-between">
                  <div className="font-medium text-slate-900">{p.name}</div>
                  <div className="text-xs text-slate-500">
                    {p.credit_amount} kredi
                  </div>
                </div>
                <div className="text-xl font-semibold tabular-nums">
                  {p.price} {p.currency || 'TRY'}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="self-start"
                  disabled={buyingPackageId === p.id}
                  onClick={() => handleBuyRequest(p)}
                  data-testid={`credit-package-buy-btn-${p.id}`}
                >
                  {buyingPackageId === p.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Satın alma talebi'
                  )}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {history.length > 0 && (
        <Card data-testid="credit-panel-history">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="h-4 w-4 text-slate-500" />
              Son Hareketler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5 text-sm">
              {history.slice(0, 8).map((h) => (
                <li
                  key={h.id}
                  className="flex justify-between gap-3 text-slate-600"
                  data-testid={`credit-history-row-${h.id}`}
                >
                  <span className="truncate">
                    {humanLedgerType(h.type)} —{' '}
                    {h.feature_key ? humanFeatureKey(h.feature_key) : (h.note || '')}
                  </span>
                  <span
                    className={`tabular-nums font-medium ${h.amount > 0 ? 'text-emerald-700' : h.amount < 0 ? 'text-red-700' : 'text-slate-400'}`}
                  >
                    {h.amount > 0 ? '+' : ''}{h.amount}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function mapRedeemError(err) {
  if (!(err instanceof ApiError)) {
    return err?.message || 'Kod kullanılırken bir hata oluştu.';
  }
  switch (err.status) {
    case 404:
      return 'Geçersiz kod.';
    case 410:
      return 'Bu kodun süresi dolmuş.';
    case 409:
      return 'Bu kod daha önce kullanılmış.';
    case 403:
      return 'Bu kod sizin hesap türünüz için geçerli değil.';
    default:
      return err.message || 'Kod kullanılırken bir hata oluştu.';
  }
}

function humanLedgerType(t) {
  const map = {
    purchase: 'Satın alma',
    admin_grant: 'Admin tarafından verildi',
    super_admin_grant: 'Süper admin tarafından verildi',
    promo_code: 'Promosyon kodu',
    free_4k_usage: 'Ücretsiz 4K kullanımı',
    reserved: 'Rezerve edildi',
    consumed: 'Tüketildi',
    refunded: 'İade edildi',
    failed_generation_refund: 'Başarısız üretim iadesi',
    unlimited_usage: 'Sınırsız kullanım',
    manual_adjustment: 'Manuel düzenleme',
  };
  return map[t] || t;
}

function humanFeatureKey(f) {
  const map = {
    babyface_4k_generation: '4K Bebek Yüzü',
    video_generation: 'Video Animasyon',
    scan_enhancement: 'Tarama İyileştirme',
    premium_report: 'Premium Rapor',
    custom_component_usage: 'Özel Bileşen',
  };
  return map[f] || f;
}
